import { useState, useEffect } from 'react';
import { BookOpen, ChevronDown, ChevronRight, Lightbulb, Target, CheckCircle, Eye, EyeOff, Clock, Languages } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { launchConfetti, showAchievement } from '../utils/effects.js';

import './Exercise.css';
import './GrammarNotes.css';

// 1. Objeto de UI para traducciones estáticas
const uiStrings = {
    en: {
        loading: 'Loading...',
        defaultTitle: 'Grammar Notes',
        instruction: 'Expand the sections to learn.',
        sections: 'Sections',
        completed: 'Completed',
        language: 'Language',
        sectionDefault: 'Section',
        read: '✓ Read',
        contentUnavailable: 'Content not available.',
        markAsRead: 'Mark as read',
        completedExcl: 'Completed!',
        excellentWork: 'Excellent work!',
        allSectionsCompleted: 'You have completed all sections. Total time:'
    },
    es: {
        loading: 'Cargando...',
        defaultTitle: 'Notas de Gramática',
        instruction: 'Expande las secciones para aprender.',
        sections: 'Secciones',
        completed: 'Completado',
        language: 'Idioma',
        sectionDefault: 'Sección',
        read: '✓ Leído',
        contentUnavailable: 'Contenido no disponible.',
        markAsRead: 'Marcar como leído',
        completedExcl: '¡Completado!',
        excellentWork: '¡Excelente trabajo!',
        allSectionsCompleted: 'Has completado todas las secciones. Tiempo total:'
    }
};

function GrammarNotes({ exerciseData, onCorrectAnswer }) {
    const isBilingual = exerciseData && exerciseData.content_en;
    const [language, setLanguage] = useState('es');

    useEffect(() => {
        if (isBilingual) {
            setLanguage('en');
        } else {
            setLanguage('es');
        }
    }, [isBilingual, exerciseData]);

    const ui = uiStrings[language];
    const currentContent = isBilingual ? exerciseData[`content_${language}`] : exerciseData;
    const safeContent = currentContent || { title: ui.loading, points: [] };

    const [expandedSections, setExpandedSections] = useState(new Set([0]));
    const [completedSections, setCompletedSections] = useState(new Set());
    const [showHighlights, setShowHighlights] = useState(true);
    const [readingTime, setReadingTime] = useState(0);
    const [isRunning, setIsRunning] = useState(true);

    useEffect(() => {
        let interval = null;
        if (isRunning) {
            interval = setInterval(() => {
                setReadingTime(time => time + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isRunning]);

    const totalSections = safeContent.points?.length || 0;
    
    useEffect(() => {
        setCompletedSections(new Set());
        setExpandedSections(new Set([0]));
        setReadingTime(0);
        setIsRunning(true);
        setLanguage(isBilingual ? 'en' : 'es'); 
    }, [exerciseData, isBilingual]); 

    useEffect(() => {
        if (completedSections.size === totalSections && totalSections > 0 && isRunning) {
            setIsRunning(false);
            onCorrectAnswer();
            launchConfetti();
            showAchievement(ui.completedExcl, '📚');
        }
    }, [completedSections, totalSections, onCorrectAnswer, isRunning, ui.completedExcl]);

    const toggleSection = (index) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) { newSet.delete(index); } else { newSet.add(index); }
            return newSet;
        });
    };

    const markSectionAsRead = (index) => {
        setCompletedSections(prev => {
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
        });
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const toggleLanguage = () => {
        setLanguage(lang => (lang === 'en' ? 'es' : 'en'));
    };
    
    // Función para manejar la accesibilidad del teclado en el div
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            toggleLanguage();
        }
    };

    const progressPercentage = totalSections > 0 ? (completedSections.size / totalSections) * 100 : 0;
    const isReadingMode = expandedSections.size > 0;

    return (
        <div className="exercise-container">
            <div className={`gn-container ${showHighlights ? '' : 'hide-highlights'} ${isReadingMode ? 'is-reading' : ''}`}>
                <header className="gn-header">
                    <div className="gn-main-header-content">
                        <div className="gn-icon-container-inset gn-main-book-icon"><BookOpen size={24} className="gn-main-icon" /></div>
                        <h2 className="gn-main-title">{safeContent.title || ui.defaultTitle}</h2>
                        <p className="gn-main-subtitle">{safeContent.subtitle || ''}</p>
                        <p className="gn-main-instruction">{ui.instruction}</p>
                    </div>
                    <div className="gn-controls-section">
                        <div className="gn-stat-box-inset"><Clock size={18} /><span>{formatTime(readingTime)}</span></div>
                        <button onClick={() => setShowHighlights(!showHighlights)} className="gn-control-btn">
                            {showHighlights ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                    </div>
                </header>
                <div className="gn-progress-container">
                    <div className="gn-progress-fill" style={{ width: `${progressPercentage}%` }} />
                </div>
                
                <div className="gn-stats-container">
                    <div className="gn-stat-box-inset large"><div className="gn-stat-number">{completedSections.size}/{totalSections}</div><div className="gn-stat-label">{ui.sections}</div></div>
                    <div className="gn-stat-box-inset large"><div className="gn-stat-number">{Math.round(progressPercentage)}%</div><div className="gn-stat-label">{ui.completed}</div></div>
                    
                    {/* --- INICIO DE LA CORRECCIÓN --- */}
                    {isBilingual && (
                        <div 
                            onClick={toggleLanguage} 
                            onKeyPress={handleKeyPress} // Accesibilidad: Activar con Enter/Espacio
                            className="gn-stat-box-inset large gn-lang-btn" 
                            title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
                            role="button" // Accesibilidad: Anunciar como botón
                            tabIndex="0" // Accesibilidad: Hacerlo enfocable
                        >
                            {/* Lógica de texto corregida */}
                            <div className="gn-stat-number">{language.toUpperCase()}</div> 
                            <div className="gn-stat-label"><Languages size={16} /> {ui.language}</div>
                        </div>
                    )}
                    {/* --- FIN DE LA CORRECCIÓN --- */}

                </div>

                <main className="gn-content">
                    <div className="gn-accordion-container">
                        {safeContent.points?.map((point, index) => {
                            const isExpanded = expandedSections.has(index);
                            const isCompleted = completedSections.has(index);
                            return (
                                <div key={index} className={`gn-accordion-item ${isExpanded ? 'is-open' : ''}`}>
                                    <div onClick={() => toggleSection(index)} className="gn-accordion-header">
                                        <div className="gn-header-left">
                                            <div className={`gn-status-icon ${isCompleted ? 'completed' : ''}`}>
                                                {isCompleted ? <CheckCircle size={20} color="white" /> : <Lightbulb size={20} color="#000" />}
                                            </div>
                                            <h4 className="gn-section-title">{point.header || ui.sectionDefault}</h4>
                                        </div>
                                        <div className="gn-header-right">
                                            {isCompleted && <div className="gn-tag-completed">{ui.read}</div>}
                                            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                        </div>
                                    </div>
                                    {isExpanded && (
                                        <div className="gn-accordion-content">
                                            <hr className="gn-separator" />
                                            <div className="gn-markdown-content">
                                                <ReactMarkdown components={{
                                                    strong: ({node, ...props}) => <strong className="gn-highlight-text" {...props} />,
                                                    em: ({node, ...props}) => <em className="gn-italic-text" {...props} />,
                                                    li: ({node, ...props}) => <li className="gn-bullet-point" {...props} />
                                                }}>
                                                    {point.content || ui.contentUnavailable}
                                                </ReactMarkdown>
                                            </div>
                                            <hr className="gn-separator" />
                                            <button onClick={() => markSectionAsRead(index)} disabled={isCompleted} className="gn-btn-mark-read">
                                                {isCompleted ? <><CheckCircle size={18} /> {ui.completedExcl}</> : <><Target size={18} /> {ui.markAsRead}</>}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </main>
                {progressPercentage === 100 && (
                    <footer className="gn-completion-footer">
                        <div className="gn-completion-icon">🎉</div>
                        <h3 className="gn-completion-title">{ui.excellentWork}</h3>
                        <p className="gn-completion-text">{ui.allSectionsCompleted} {formatTime(readingTime)}</p>
                    </footer>
                )}
            </div>
        </div>
    );
}

export default GrammarNotes;